import { useState, useEffect, useCallback } from "react";
import { Monitor, X, DoorOpen, Phone, MapPin, Video, Sparkles, Clock } from "lucide-react";
import api from "../services/api";

var DAYS = ["Segunda", "Terca", "Quarta", "Quinta", "Sexta", "Sabado"];
var DAYS_SHORT = ["SEG", "TER", "QUA", "QUI", "SEX", "SAB"];
var HOURS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17];
var TYPE_ICONS = { call: Phone, presencial: MapPin, video: Video };

var EVENT_COLORS = {
  feira: "#F59E0B", jantar: "#8B5CF6", palestra: "#10B981",
  treinamento: "#3B82F6", outro: "#6B7280",
};

function getMonday(d) {
  var day = d.getDay();
  var diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.getFullYear(), d.getMonth(), diff);
}

function dateToStr(d) { return d.toISOString().split("T")[0]; }

function fmtTime(iso) {
  try { return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }); } catch(e) { return ""; }
}

function fmtDateShort(d) {
  return d.getDate().toString().padStart(2, "0") + "/" + (d.getMonth() + 1).toString().padStart(2, "0");
}

export default function AgendaTVPage() {
  const [schedules, setSchedules] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [events, setEvents] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [now, setNow] = useState(new Date());

  var monday = getMonday(now);
  var weekDates = [];
  for (var i = 0; i < 6; i++) {
    weekDates.push(new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i));
  }

  var fetchAll = useCallback(async function() {
    try {
      var mon = getMonday(new Date());
      var dates = [];
      for (var j = 0; j < 6; j++) dates.push(new Date(mon.getFullYear(), mon.getMonth(), mon.getDate() + j));
      var start = dateToStr(dates[0]);
      var end = dateToStr(dates[5]);
      var res = await Promise.all([
        api.get("/schedule?limit=100"),
        api.get("/room/bookings?start_date=" + start + "&end_date=" + end),
        api.get("/events?start_date=" + start + "&end_date=" + end),
        api.get("/room/blocks"),
      ]);
      setSchedules(res[0].data.schedules || []);
      setRooms(res[1].data.bookings || []);
      setEvents(res[2].data.events || []);
      setBlocks(res[3].data.blocks || []);
    } catch(e) { console.error(e); }
  }, []);

  useEffect(function() { fetchAll(); }, [fetchAll]);

  // Auto-refresh every 60s + clock update every 30s
  useEffect(function() {
    var dataTimer = setInterval(fetchAll, 60000);
    var clockTimer = setInterval(function() { setNow(new Date()); }, 30000);
    return function() { clearInterval(dataTimer); clearInterval(clockTimer); };
  }, [fetchAll]);

  var currentHour = now.getHours();
  var currentMin = now.getMinutes();
  var todayStr = dateToStr(now);

  // Build week start/end string for header
  var weekLabel = fmtDateShort(weekDates[0]) + " a " + fmtDateShort(weekDates[5]);
  var clockLabel = now.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

  // All-day events (events that span full days)
  var allDayEvents = [];
  events.forEach(function(ev) {
    weekDates.forEach(function(wd, idx) {
      var wdStr = dateToStr(wd);
      if (ev.start_date <= wdStr && (ev.end_date || ev.start_date) >= wdStr) {
        allDayEvents.push({ ...ev, dayIdx: idx });
      }
    });
  });

  function exitTV() {
    window.history.back();
  }

  return (
    <div className="fixed inset-0 bg-[#0B1120] text-white overflow-hidden flex flex-col" data-testid="agenda-tv-page">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 bg-[#0B1120] border-b border-white/5 flex-shrink-0">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#3B82F6] flex items-center justify-center">
              <Monitor className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-bold tracking-tight">Methos Corretora</span>
          </div>
          <span className="text-white/30">|</span>
          <span className="text-sm text-white/50">Agenda da Semana</span>
          <span className="px-3 py-1 rounded-full bg-white/5 text-sm text-white/60 font-medium">{weekLabel}</span>
        </div>
        <div className="flex items-center gap-4">
          {/* Legend */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#3B82F6]" /><span className="text-xs text-white/40">Reuniao</span></div>
            <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" /><span className="text-xs text-white/40">Sala</span></div>
            <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-violet-500" /><span className="text-xs text-white/40">Evento</span></div>
          </div>
          <span className="text-white/20">|</span>
          <div className="flex items-center gap-2 text-white/60">
            <Clock className="w-4 h-4" />
            <span className="text-2xl font-bold tabular-nums" data-testid="tv-clock">{clockLabel}</span>
          </div>
          <button onClick={exitTV}
            className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/40 hover:text-white transition-colors"
            data-testid="tv-exit-button">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* All-day events bar */}
      {allDayEvents.length > 0 && (
        <div className="px-6 py-2 bg-[#0B1120] border-b border-white/5 flex-shrink-0">
          <div className="grid grid-cols-6 gap-2">
            {weekDates.map(function(wd, idx) {
              var dayEvents = allDayEvents.filter(function(e) { return e.dayIdx === idx; });
              return (
                <div key={idx} className="flex flex-col gap-1">
                  {dayEvents.map(function(ev) {
                    var color = EVENT_COLORS[ev.event_type] || "#6B7280";
                    return (
                      <div key={ev.id + "-" + idx} className="flex items-center gap-1.5 px-2 py-1 rounded-md" style={{ backgroundColor: color + "20", borderLeft: "3px solid " + color }} data-testid={"tv-event-" + ev.id}>
                        <Sparkles className="w-3 h-3 flex-shrink-0" style={{ color: color }} />
                        <span className="text-xs font-medium truncate" style={{ color: color }}>{ev.title}</span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Week Grid */}
      <div className="flex-1 overflow-hidden flex flex-col px-6 py-3">
        {/* Day headers */}
        <div className="grid grid-cols-[60px_repeat(6,1fr)] gap-0 flex-shrink-0 mb-1">
          <div /> {/* hour column spacer */}
          {weekDates.map(function(wd, idx) {
            var wdStr = dateToStr(wd);
            var isToday = wdStr === todayStr;
            var isBlocked = blocks.some(function(b) { return b.start_date <= wdStr && b.end_date >= wdStr; });
            return (
              <div key={idx} className={"text-center py-2 rounded-lg mx-0.5 " + (isToday ? "bg-[#3B82F6]/20" : "")} data-testid={"tv-day-header-" + idx}>
                <p className={"text-xs font-bold tracking-widest " + (isToday ? "text-[#3B82F6]" : "text-white/30")}>{DAYS_SHORT[idx]}</p>
                <p className={"text-xl font-bold " + (isToday ? "text-[#3B82F6]" : "text-white/80")}>{wd.getDate()}</p>
                {isBlocked && <p className="text-[10px] text-red-400 font-medium mt-0.5">BLOQUEADO</p>}
              </div>
            );
          })}
        </div>

        {/* Time grid */}
        <div className="flex-1 overflow-y-auto relative scrollbar-thin" data-testid="tv-time-grid">
          <div className="grid grid-cols-[60px_repeat(6,1fr)] gap-0 relative">
            {/* Hour labels + rows */}
            {HOURS.map(function(hour) {
              return (
                <div key={hour} className="contents">
                  <div className="h-16 flex items-start justify-end pr-3 pt-0.5">
                    <span className="text-xs text-white/20 font-medium tabular-nums">{hour.toString().padStart(2, "0")}:00</span>
                  </div>
                  {weekDates.map(function(wd, dayIdx) {
                    var wdStr = dateToStr(wd);
                    var isToday = wdStr === todayStr;
                    var maxHour = dayIdx < 5 ? 18 : 12;
                    var isBusinessHour = hour < maxHour;

                    // Find items for this cell
                    var cellSchedules = schedules.filter(function(s) {
                      if (s.status === "cancelado") return false;
                      try {
                        var sd = new Date(s.date);
                        var sdDate = sd.toISOString().split("T")[0];
                        var sdHour = sd.getUTCHours();
                        return sdDate === wdStr && sdHour === hour;
                      } catch(e) { return false; }
                    });
                    var cellRooms = rooms.filter(function(r) {
                      return r.date === wdStr && r.start_hour <= hour && (r.start_hour + (r.duration_hours || 1)) > hour;
                    });

                    return (
                      <div key={dayIdx}
                        className={"h-16 border-t mx-0.5 relative " +
                          (isToday ? "border-white/8 bg-white/[0.02]" : "border-white/5") +
                          (!isBusinessHour ? " opacity-20" : "")}>
                        {/* Schedules */}
                        {cellSchedules.map(function(s) {
                          var Icon = TYPE_ICONS[s.schedule_type] || Phone;
                          var isRealizado = s.status === "realizado";
                          return (
                            <div key={s.id}
                              className={"absolute inset-x-1 top-0.5 rounded-md px-2 py-1 overflow-hidden " + (isRealizado ? "bg-emerald-500/40 border border-emerald-400/50" : "bg-[#3B82F6]/40 border border-[#3B82F6]/50")}
                              style={{ height: "calc(100% - 4px)" }}
                              data-testid={"tv-schedule-" + s.id}>
                              <div className="flex items-center gap-1">
                                <Icon className={"w-3 h-3 flex-shrink-0 " + (isRealizado ? "text-emerald-300" : "text-blue-300")} />
                                <span className={"text-xs font-semibold truncate " + (isRealizado ? "text-emerald-200" : "text-blue-200")}>{s.client_name}</span>
                              </div>
                              <p className="text-[10px] text-white/50 truncate">{fmtTime(s.date)} · {s.broker_name || ""}</p>
                            </div>
                          );
                        })}
                        {/* Room bookings */}
                        {cellRooms.map(function(r) {
                          var isFirstHour = r.start_hour === hour;
                          if (!isFirstHour) return null;
                          var cellHeight = (r.duration_hours || 1) * 64;
                          return (
                            <div key={r.id}
                              className="absolute inset-x-1 top-0.5 rounded-md px-2 py-1 bg-amber-500/40 border border-amber-400/50 overflow-hidden z-10"
                              style={{ height: (cellHeight - 4) + "px" }}
                              data-testid={"tv-room-" + r.id}>
                              <div className="flex items-center gap-1">
                                <DoorOpen className="w-3 h-3 text-amber-300 flex-shrink-0" />
                                <span className="text-xs font-semibold text-amber-200 truncate">{r.client_name}</span>
                              </div>
                              <p className="text-[10px] text-amber-300/70 truncate">Sala · {r.broker_name}</p>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            })}

            {/* Current time indicator */}
            {currentHour >= 8 && currentHour < 18 && (
              <div className="absolute left-[60px] right-0 pointer-events-none z-20"
                style={{ top: ((currentHour - 8) * 64 + (currentMin / 60) * 64) + "px" }}
                data-testid="tv-current-time-line">
                <div className="flex items-center">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500 -ml-1 shadow-lg shadow-red-500/40" />
                  <div className="flex-1 h-[2px] bg-red-500/60" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-2 border-t border-white/5 flex items-center justify-between flex-shrink-0">
        <span className="text-xs text-white/20">Atualiza automaticamente a cada 60s</span>
        <div className="flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-xs text-white/20">Ao vivo</span>
        </div>
      </div>
    </div>
  );
}
